// ─────────────────────────────────────────────────────────────
// ScriptHelperApp — root for the main app route (/ and /discoverstudio).
// Name kept as-is post-rebrand: internal identifiers are unchanged, only
// display strings moved to "Maps Discover Studio".
// Owns: login state machine, tab routing, editor mode, theme.
// Shows LoginScreen until the user has a validated session.
// On mount, checks localStorage for a token and validates it
// against GET /api/auth/me. Clears stale tokens on 401.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box, Snackbar, Alert } from "@mui/material";
import { useColorMode } from "../hooks/useColorMode";
import { useMaintenance } from "../hooks/useMaintenance";
import { createDashboardTheme } from "../theme";
import { LoginScreen } from "./scriptHelper/LoginScreen";
import { AppHeader } from "./scriptHelper/AppHeader";
import { WelcomeTab } from "./scriptHelper/WelcomeTab";
import { PythonTab } from "./scriptHelper/PythonTab";
import { OutputTab } from "./scriptHelper/OutputTab";
import { ImageLibraryTab } from "./scriptHelper/ImageLibraryTab";
import { ScriptsTab } from "./scriptHelper/ScriptsTab";
import { CommunityTab } from "./scriptHelper/CommunityTab";
import { HelpTab } from "./scriptHelper/HelpTab";
import { SettingsTab } from "./scriptHelper/SettingsTab";
import { AiPanel, extractFirstName } from "./scriptHelper/AiPanel";
import { LoggingNoticeModal } from "./scriptHelper/LoggingNoticeModal";
import { MaintenanceScreen } from "./MaintenanceScreen";
import { MaintenanceBanner } from "../components/MaintenanceBanner";
import { authApi, executeApi, marieApi, TierLimitError } from "../api/client";
import type { AuthUser, CommunityScript, ExecutionResult } from "../api/client";
import type { ChatMessage, EditorMode, ScriptHelperTab } from "./scriptHelper/types";
import { AI_MODELS } from "./scriptHelper/types";

// Placeholder initial messages for tabs that use the shared AI panel
function marieGreeting(userName?: string | null): string {
  const firstName = extractFirstName(userName ?? null);
  const hi = firstName ? `Hi ${firstName}!` : "Hello!";
  return `${hi} I'm Marie, your AI coding assistant for the Maps microscopy platform. I can help you write and debug Python scripts using the Maps Script Bridge API. What would you like to work on?`;
}

const INITIAL_GREETING = marieGreeting();

const WELCOME_AI_MESSAGES: ChatMessage[] = [
  { id: "1", role: "assistant", content: INITIAL_GREETING },
];

const PYTHON_AI_MESSAGES: ChatMessage[] = [
  { id: "1", role: "assistant", content: INITIAL_GREETING },
];

const EDITOR_MODE_KEY = "editor_mode";

// ── Marie selected-model persistence ────────────────────────
// The picker selection is lifted to ScriptHelperApp so every AiPanel instance
// (PythonTab Code-mode sidebar, PythonTab Chat full-width, OutputTab sidebar)
// shares one value. Greg's bug (2026-05-15): switching to GPT-5.4 in the
// Python tab, then running, then watching the auto-switch to Output reset
// the picker back to Opus 4.7 — each AiPanel used to own its own state.
//
// The id is also persisted to localStorage so a refresh / fresh sign-in
// brings the user back to whatever they last picked. Falls back to the
// hard-coded default "claude-opus-4-7" on first load, and to the first
// allowed model when the persisted id isn't in the user's whitelist
// (e.g. a basic user with a previously-cached advanced selection).
const MARIE_MODEL_KEY = "marie_selected_model";
const MARIE_MODEL_DEFAULT = "claude-opus-4-7";

/** Read the persisted Marie model id (or the hard default on first run). */
function readPersistedMarieModel(): string {
  try {
    const stored = localStorage.getItem(MARIE_MODEL_KEY);
    return stored ?? MARIE_MODEL_DEFAULT;
  } catch {
    // localStorage can throw in private-browse / disabled-cookie modes.
    return MARIE_MODEL_DEFAULT;
  }
}

/**
 * Reconcile a candidate model id against the user's allowed list. Returns
 * the candidate when it's both in AI_MODELS and (when a whitelist is in
 * force) in `allowedModels`. Otherwise returns the first allowed catalog
 * entry, or — as a last resort — the hard-coded default. Used at hydrate
 * time and whenever `allowedModels` changes (e.g. role demotion).
 */
function resolveAllowedMarieModel(
  candidate: string,
  allowedModels: string[] | null | undefined,
): string {
  const inCatalog = (id: string): boolean => AI_MODELS.some((m) => m.id === id);
  const allow = allowedModels && allowedModels.length > 0
    ? new Set(allowedModels)
    : null;
  if (inCatalog(candidate) && (!allow || allow.has(candidate))) {
    return candidate;
  }
  // Candidate rejected — pick the first AI_MODELS entry the user can use.
  const firstAllowed = allow
    ? AI_MODELS.find((m) => allow.has(m.id))?.id
    : AI_MODELS[0]?.id;
  return firstAllowed ?? MARIE_MODEL_DEFAULT;
}

export function ScriptHelperApp() {
  const [colorMode, toggleColorMode] = useColorMode();
  const theme = useMemo(() => createDashboardTheme(colorMode), [colorMode]);
  const navigate = useNavigate();

  // null  = not yet checked (show nothing / loading)
  // false = no valid session (show login screen)
  // AuthUser = valid session
  const [currentUser, setCurrentUser] = useState<AuthUser | null | false>(null);

  // Site-wide maintenance state. Polls in the background; flips on
  // a 503-with-maintenance from any in-flight request.
  const maintenance = useMaintenance();

  // Error message to surface on the login screen after a failed SSO redirect.
  const [ssoError, setSsoError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<ScriptHelperTab>("welcome");

  // Code|Chat toggle — persisted in localStorage so the user's preference
  // survives page refreshes. Default is "code".
  const [editorMode, setEditorMode] = useState<EditorMode>(() => {
    const stored = localStorage.getItem(EDITOR_MODE_KEY);
    return stored === "chat" ? "chat" : "code";
  });

  // Lifted Marie model selection — shared by every AiPanel instance and
  // persisted to localStorage. Hydration uses the raw persisted value (we
  // don't yet know the user's allowed_models at first render — currentUser
  // is null until /api/auth/me resolves). The whitelist reconciliation runs
  // in a useEffect once currentUser arrives, swapping to a safe fallback
  // when the persisted id isn't actually usable for this user.
  const [selectedMarieModel, setSelectedMarieModel] = useState<string>(() =>
    readPersistedMarieModel(),
  );

  // Shared AI panel messages — lifted here so they persist across tab switches
  // and mode switches without remounting AiPanel.
  const [welcomeAiMessages, setWelcomeAiMessages] = useState<ChatMessage[]>(WELCOME_AI_MESSAGES);
  const [pythonAiMessages, setPythonAiMessages] = useState<ChatMessage[]>(PYTHON_AI_MESSAGES);

  // Once the user is known, personalise the opening greeting — but only if the
  // conversation is still at the single initial message (hasn't started yet).
  useEffect(() => {
    if (!currentUser) return;
    const personalised = marieGreeting((currentUser as any).full_name);
    const update = (msgs: ChatMessage[]): ChatMessage[] =>
      msgs.length === 1 && msgs[0].id === "1"
        ? [{ ...msgs[0], content: personalised }]
        : msgs;
    setWelcomeAiMessages((m) => update(m));
    setPythonAiMessages((m) => update(m));
  }, [currentUser]);

  // Reconcile the persisted Marie model against the signed-in user's
  // allowed_models. Runs once /api/auth/me resolves (currentUser becomes an
  // object) and any time the user's allowlist changes (role demotion via
  // admin panel, fresh sign-in as a different tier). When the current
  // selection is disallowed, silently fall back to the first allowed id —
  // the brief is "no crash, no error toast, just a quiet swap".
  useEffect(() => {
    if (typeof currentUser !== "object" || currentUser === null) return;
    const allowed = currentUser.allowed_models ?? null;
    const resolved = resolveAllowedMarieModel(selectedMarieModel, allowed);
    if (resolved !== selectedMarieModel) {
      setSelectedMarieModel(resolved);
    }
  }, [currentUser, selectedMarieModel]);

  // Persist the Marie model selection so a refresh keeps the user on their
  // last picked model. We only write once a real signed-in selection is in
  // play; that's the steady state — initial mounts read the same key back.
  useEffect(() => {
    try {
      localStorage.setItem(MARIE_MODEL_KEY, selectedMarieModel);
    } catch {
      // Ignore localStorage failures (private browsing, quota, etc.).
    }
  }, [selectedMarieModel]);

  // Placeholder container width for AI panel snap calculations
  // Phase 1: wire up a ResizeObserver to track actual width
  const contentWidth = 1200;

  // Monaco editor ref — held here so other parts of the app can inject code
  const monacoEditorRef = useRef<any>(null);
  // Current editor code — kept in sync via onCodeChange so Run can send it
  const [currentCode, setCurrentCode] = useState<string>("");
  // Signals PythonTab to pre-select a library image. Cleared after PythonTab consumes it.
  const [preloadImageId, setPreloadImageId] = useState<number | null>(null);
  // Incremented each time an external script is loaded — signals PythonTab to update currentScriptId.
  const [scriptLoadKey, setScriptLoadKey] = useState(0);
  // The database id of the most recently loaded user script, or null for starter/new scripts.
  const [loadedScriptId, setLoadedScriptId] = useState<number | null>(null);
  // The display title of the most recently loaded script (any source), or null
  // for new/untitled. Kept strictly in sync with loadedScriptId — both reflect
  // the same loaded entity. Used by PythonTab to derive a sensible download filename.
  const [loadedScriptTitle, setLoadedScriptTitle] = useState<string | null>(null);
  // Bumped when the workspace should be reset to a clean slate (e.g. New Chat
  // in the AI panel, or New Script). PythonTab listens for changes and clears
  // its locally-owned image selection (selectedImage / tempImageFile) — those
  // pieces of state don't live up here, so a signal-style key is the cleanest
  // way to trigger them without lifting unrelated state.
  const [workspaceClearKey, setWorkspaceClearKey] = useState(0);

  // ScriptsTab sub-tab targeting. When CommunityTab fires onNavigateToMyScripts
  // (after a successful Fork), we set the index to 0 (My Scripts — index 0
  // after the 2026-05-15 sub-tab swap) and bump the key so the ScriptsTab
  // effect re-applies the value even if it was already 0 from a prior visit.
  // `undefined` = no explicit navigation intent; lets the ScriptsTab smart
  // default decide (My Scripts unless the user has zero saved scripts, in
  // which case it lands on Starter Scripts).
  const [scriptsInitialSubTab, setScriptsInitialSubTab] = useState<0 | 1 | undefined>(undefined);
  const [scriptsSubTabKey, setScriptsSubTabKey] = useState(0);

  // Execution state — lifted here so PythonTab triggers and OutputTab displays
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState<ExecutionResult | null>(null);
  // Input image for the most recent run — forwarded to OutputTab so it can show the original image
  const [lastRunImageId, setLastRunImageId] = useState<number | undefined>();
  const [lastRunImageFile, setLastRunImageFile] = useState<File | undefined>();

  // ── Marie session capture (Sam, 2026-05-06) ─────────────────
  // The active session id this app instance is contributing to. Captured from
  // the leading meta frame on the first chat in a session. Sent back on
  // every subsequent chat, run, and save so the backend can stitch them
  // into a single MarieSession record.
  //
  // Lifecycle resets to null when:
  //   • User clicks "Start new chat" in the Marie panel
  //   • A different saved script is loaded
  //   • User clicks New Script
  //   • User signs out / signs in fresh
  // The next chat after a reset carries `new_session: true`, so the backend
  // opens a brand-new session and echoes its id back via the meta frame.
  const [marieSessionId, setMarieSessionId] = useState<number | null>(null);

  // Bumped on each reset so AiPanel knows to send `new_session: true` on the
  // very next chat. AiPanel decrements its local "armed" flag once consumed.
  const [newSessionArmKey, setNewSessionArmKey] = useState(0);
  function armNewSession() {
    setMarieSessionId(null);
    setNewSessionArmKey((k) => k + 1);
  }

  const [imageFlashKey, setImageFlashKey] = useState(0);
  // Snackbar shown when handleRun aborts because no image is selected. The
  // existing imageFlashKey-driven LeftPanel thumbnail flash is too subtle and
  // is invisible from Marie's chat panel / Chat mode, so the run looked hung.
  // This is the single chokepoint covering every Run entry point (Marie chip,
  // PythonTab toolbar Run, OutputTab Run-from-Output).
  const [noImageSnackbarOpen, setNoImageSnackbarOpen] = useState(false);
  // Tier-limit toast state (Sam, 2026-05-14). Used for both Marie chat
  // tier-rejects (forwarded from AiPanel) and script-run 429s. We don't
  // need parallel snackbars — there's only one rejected request at a
  // time — so a single state slot suffices. `severity` lets us pick
  // between "info" (model swap notice) and "warning" (quota exhausted).
  const [tierLimitSnack, setTierLimitSnack] = useState<{
    message: string;
    severity: "info" | "warning";
  } | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  // When a run fails, automatically send the error to Marie for analysis.
  // Use a ref to track which job_ids we've already sent so re-renders don't re-fire.
  const analyzedJobIds = useRef<Set<string>>(new Set());
  const [errorAnalysisRequest, setErrorAnalysisRequest] = useState<string | null>(null);
  // Tracks consecutive auto-analysis attempts for the current run. Reset on each new run.
  // Capped at 2 — if Marie can't fix it in two attempts, the user should take over.
  const autoAnalyzeCountRef = useRef(0);
  // The tab the user was on at the moment Run was triggered. handleRun always
  // navigates to "output" on completion (success AND failure), so by the time
  // the failure effect runs `activeTab` is already "output" and can't tell us
  // where the user came from. We snapshot it at run-start instead. Greg's ask
  // (2026-06-05): if the user was already in the Output tab working with Marie
  // when a run fails, keep the auto-error-analysis IN the Output tab's Marie
  // panel rather than yanking them back to the editor.
  const runOriginTabRef = useRef<ScriptHelperTab>("welcome");

  useEffect(() => {
    if (!lastResult || lastResult.status === "success") return;
    if (analyzedJobIds.current.has(lastResult.job_id)) return;
    analyzedJobIds.current.add(lastResult.job_id);
    autoAnalyzeCountRef.current += 1;
    if (autoAnalyzeCountRef.current > 2) {
      // Marie has tried twice — stop auto-analysis, let the user take over
      return;
    }
    const stdout = lastResult.stdout?.trim() ?? "";
    const stderr = lastResult.stderr?.trim() ?? "";
    const console = lastResult.console?.trim() ?? "";

    const failureType =
      lastResult.status === "timeout"
        ? "timeout (exceeded 30s limit)"
        : lastResult.exit_code < 0
        ? "infrastructure error"
        : `crash (non-zero exit code ${lastResult.exit_code})`;

    const stdoutBlock = stdout
      ? `--- STDOUT ---\n${stdout}\n`
      : "--- STDOUT ---\n(no stdout)\n";
    const stderrBlock = stderr
      ? `--- STDERR ---\n${stderr}\n`
      : stdout || console
      ? "--- STDERR ---\n(no stderr — error info may be in stdout)\n"
      : "--- STDERR ---\n(no stderr)\n";

    // If neither stdout nor stderr came through (infrastructure failure paths),
    // fall back to the merged console blob so we don't leave Marie with nothing.
    const haveSeparated = Boolean(stdout || stderr);
    const fallback =
      !haveSeparated && console
        ? `\n--- CONSOLE (merged fallback) ---\n${console}\n`
        : "";

    const errorText =
      stdout || stderr || console || "Script failed with no console output.";

    const prompt =
      haveSeparated || console
        ? [
            "The script failed. Analyze what went wrong and propose a corrected version.",
            "",
            `Exit code: ${lastResult.exit_code}`,
            `Duration: ${lastResult.duration_ms}ms`,
            `Failure type: ${failureType}`,
            "",
            stdoutBlock,
            stderrBlock,
            fallback,
          ]
            .filter(Boolean)
            .join("\n")
        : `The script failed with the following error output. Please analyse what went wrong and propose a corrected version of the script:\n\n${errorText}`;

    setErrorAnalysisRequest(prompt);
    // Stay on Output if that's where the user triggered the run from — the
    // Output tab's Marie panel consumes errorAnalysisRequest in that case
    // (see the activeTab-gated wiring below). Otherwise fall back to today's
    // behavior: surface the analysis in the editor (Python) tab.
    if (runOriginTabRef.current !== "output") {
      setActiveTab("python");
    }
    // Removed: setEditorMode("chat") — don't force mode switch on failure
  }, [lastResult]);

  function handleEditorReady(editorRef: React.MutableRefObject<any>) {
    monacoEditorRef.current = editorRef.current;
  }

  function handleRunFromOutput() {
    const code = monacoEditorRef.current?.getValue() ?? currentCode ?? "";
    handleRun(code, lastRunImageId, lastRunImageFile);
  }

  async function handleRun(scriptCode: string, imageId?: number, imageFile?: File, parameters?: string) {
    if (isRunning) return;
    if (!imageId && !imageFile) {
      // Keep the LeftPanel thumbnail flash for users with that panel visible…
      setImageFlashKey((k) => k + 1);
      // …and surface a clear toast so the run never looks hung from Marie's
      // chat panel or Chat mode (where the LeftPanel isn't on screen at all).
      setNoImageSnackbarOpen(true);
      return;
    }
    // Reset per-run auto-analysis counter so a new manual run gets its own 2 attempts
    autoAnalyzeCountRef.current = 0;
    // Snapshot the tab the run was launched from, BEFORE we navigate to Output
    // on completion. The failure effect reads this to decide whether to keep
    // the auto-error-analysis on Output (origin === "output") or route it to
    // the editor (everywhere else — today's behavior).
    runOriginTabRef.current = activeTab;
    setIsRunning(true);
    setLastRunImageId(imageId);
    setLastRunImageFile(imageFile);
    try {
      // marie_session_id is purely additive — only sent when a session is
      // active. Backend treats absence as a standalone run.
      const result = await executeApi.run(
        scriptCode,
        imageId,
        imageFile,
        parameters,
        marieSessionId,
      );
      setLastResult(result);
      setActiveTab("output");
    } catch (err) {
      // Tier-limit rejects (Sam, 2026-05-14): basic-tier users get a 429
      // with `kind: "script_run"` after the hourly cap. The run was never
      // started — surface the friendly copy as a bottom-center Snackbar
      // (same shape as the "no image selected" toast Greg already liked)
      // and DON'T pollute the Output tab with a fake failure row. The
      // Run button stays available — limit is per-hour, may unlock soon.
      if (err instanceof TierLimitError) {
        if (
          err.detail.code === "quota_exceeded" &&
          err.detail.kind === "script_run"
        ) {
          setTierLimitSnack({
            message: err.detail.message,
            severity: "warning",
          });
          return;
        }
        // Any other TierLimitError shape here would be unexpected from
        // /api/execute — but defensively still surface it as a toast
        // rather than letting it fall through to the fake-failure row.
        setTierLimitSnack({
          message: err.detail.message,
          severity: "warning",
        });
        return;
      }
      // Surface the error as a failed result so OutputTab can display it
      setLastResult({
        job_id: "error",
        status: "failure",
        exit_code: 1,
        console: err instanceof Error ? err.message : "Execution failed",
        duration_ms: 0,
        output_files: [],
      });
      setActiveTab("output");
    } finally {
      setIsRunning(false);
    }
  }

  // ── Persist editorMode to localStorage ────────────────────
  useEffect(() => {
    localStorage.setItem(EDITOR_MODE_KEY, editorMode);
  }, [editorMode]);

  // ── On mount: handle SSO redirects, then validate stored token ──
  //
  // Order of operations matters here:
  //   1. Check URL for ?token= or ?auth_error= (SSO return)
  //   2. If ?token= present, write it to localStorage BEFORE the auth
  //      check below, so the rest of the flow treats it as a normal
  //      stored token and calls authApi.me() to fetch the full profile.
  //   3. Strip the query params from the URL either way.
  //   4. Run the normal token validation flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("token");
    const ssoErrorParam = params.get("auth_error");

    // ── SSO success path ──────────────────────────────────────
    if (ssoToken) {
      localStorage.setItem("auth_token", ssoToken);
      // Strip the token from the URL immediately so it doesn't linger
      // in browser history or get copy-pasted accidentally.
      window.history.replaceState({}, "", window.location.pathname);
    }

    // ── SSO failure path ──────────────────────────────────────
    if (ssoErrorParam) {
      let decoded = ssoErrorParam;
      try {
        decoded = decodeURIComponent(ssoErrorParam);
      } catch {
        // If decoding fails, use the raw value — it's still readable.
      }
      window.history.replaceState({}, "", window.location.pathname);

      // Special case: backend redirects with auth_error=maintenance when a
      // non-admin SSO user lands during maintenance. Flip into the
      // maintenance screen state directly. We don't have the message in
      // the redirect URL, so re-fetch /api/system/maintenance to get the
      // current admin-set message + timestamp.
      if (decoded === "maintenance") {
        void maintenance.refresh();
        setCurrentUser(false);
        return;
      }

      setSsoError(decoded);
      setCurrentUser(false);
      return;
    }

    // ── Normal token validation ───────────────────────────────
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setCurrentUser(false);
      return;
    }

    // Try to restore from stored user first so there's no flash,
    // then validate in the background with the server.
    const stored = localStorage.getItem("auth_user");
    if (stored) {
      try {
        setCurrentUser(JSON.parse(stored) as AuthUser);
      } catch {
        // malformed — fall through to server validation below
      }
    }

    authApi.me()
      .then((user) => {
        localStorage.setItem("auth_user", JSON.stringify(user));
        setCurrentUser(user);
      })
      .catch(() => {
        // Token is invalid or expired — clear storage and show login
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        setCurrentUser(false);
      });
  }, []);

  function handleLogin(user: AuthUser) {
    setCurrentUser(user);
    setActiveTab("welcome");
    // If the user used the "Admin? Sign in here" affordance during
    // maintenance, re-fetch the gate so admins see the banner-and-app
    // view instead of the login form. Non-admins who somehow logged in
    // here would still be re-blocked by the next API 503.
    if (maintenance.state?.adminBypass) {
      void maintenance.refresh();
      maintenance.setAdminBypass(false);
    }
  }

  async function handleSignOut() {
    try {
      await authApi.logout();
    } catch {
      // proceed regardless
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem(EDITOR_MODE_KEY);
    setCurrentUser(false);
    setActiveTab("welcome");
    setEditorMode("code");
    // New user / fresh login → next chat opens a fresh session.
    armNewSession();
  }

  function handleSettingsClick() {
    setActiveTab("settings");
  }

  // Welcome-video flag flip. WelcomeTab calls this twice:
  //   1. Optimistically with `has_seen_welcome_video: true` (so the hero
  //      panel collapses immediately on play / skip-click).
  //   2. Again with the server's authoritative AuthUser once
  //      POST /api/users/me/welcome-video-seen resolves.
  // Both calls go through the same writer so localStorage stays in sync —
  // a page refresh after step 1 (but before step 2) still picks up the
  // optimistic flip, and step 2 just overwrites with identical data.
  function handleWelcomeVideoSeen(updated: AuthUser) {
    setCurrentUser(updated);
    localStorage.setItem("auth_user", JSON.stringify(updated));
  }

  // One-time data-logging notice (Feature 2). Dismissal flips the flag
  // optimistically — locally AND in localStorage — so the modal closes
  // instantly and never re-shows this session, then fires the idempotent
  // acknowledge endpoint. If the network call fails we keep the optimistic
  // flip for the session (the user isn't trapped); a future session will
  // re-hydrate from /api/auth/me and re-show, which the brief accepts.
  function handleAcknowledgeLoggingNotice() {
    setCurrentUser((prev) =>
      typeof prev === "object" && prev !== null
        ? { ...prev, logging_notice_acknowledged: true }
        : prev,
    );
    try {
      const stored = localStorage.getItem("auth_user");
      if (stored) {
        const parsed = JSON.parse(stored) as AuthUser;
        localStorage.setItem(
          "auth_user",
          JSON.stringify({ ...parsed, logging_notice_acknowledged: true }),
        );
      }
    } catch {
      // Ignore localStorage parse/write failures — the in-state flip already
      // closed the modal for this session.
    }
    authApi
      .acknowledgeLoggingNotice()
      .then((updated) => {
        // Reconcile with the server's authoritative user object.
        setCurrentUser(updated);
        localStorage.setItem("auth_user", JSON.stringify(updated));
      })
      .catch(() => {
        // Already closed optimistically — nothing more to do.
      });
  }

  function handleTabChange(tab: ScriptHelperTab) {
    setActiveTab(tab);
  }

  function handleAiPrompt(prompt: string) {
    setActiveTab("python");
    setEditorMode("chat");
    setPendingPrompt(prompt);
  }

  function handleLoadScript(
    code: string,
    defaultImageId?: number | null,
    savedScriptId?: number | null,
    scriptTitle?: string,
  ) {
    // Loading a script (starter, My Scripts, community) is a clean break.
    // Reset the whole workspace first — empty editor, no loaded script,
    // image deselected, chat back to the initial greeting, new Marie session.
    // The setters below then repopulate state with the new script's values
    // on top of the freshly-reset baseline. resetWorkspace() also calls
    // armNewSession() so we don't need a second one here.
    resetWorkspace();

    // PythonTab is always mounted (CSS display toggle), so Monaco is always available.
    if (monacoEditorRef.current) {
      monacoEditorRef.current.setValue(code);
    }
    setCurrentCode(code);
    setLoadedScriptId(savedScriptId ?? null);
    setLoadedScriptTitle(scriptTitle ?? null);
    setScriptLoadKey((k) => k + 1);
    if (defaultImageId) {
      setPreloadImageId(defaultImageId);
    }
    setActiveTab("python");

    // Inject a Marie acknowledgement into the Python AI chat so Chat-mode users
    // see that the script has been loaded and can run it with one click.
    if (scriptTitle) {
      const hasImage = Boolean(defaultImageId);
      const content = hasImage
        ? `I've loaded "${scriptTitle}" into the editor with a matching sample image. It's ready to run.`
        : `I've loaded "${scriptTitle}" into the editor. Select an image and run it when you're ready.`;
      const ackMessage: ChatMessage = {
        id: `load-${Date.now()}`,
        role: "assistant",
        content,
        extractedCode: code,
      };
      setPythonAiMessages((msgs) => {
        // If the chat is still at its initial greeting, replace it so the
        // acknowledgement becomes the opening message. Otherwise, append.
        if (msgs.length === 1 && msgs[0].id === "1") {
          return [ackMessage];
        }
        return [...msgs, ackMessage];
      });
    }
  }

  // Community-script load — same editor-open + tab-switch as handleLoadScript,
  // but instead of the generic "I've loaded X" ack we fire the backend's
  // Marie-voiced intro endpoint and append the result as an assistant turn.
  // The intro credits the script's author so community contributions feel
  // first-class. Navigation does NOT block on the LLM call; the intro is
  // appended whenever it resolves (~1–2s typically).
  //
  // Backend always returns 200 in the steady state (source: "llm" | "fallback").
  // Only HTTP-level failures (network, 4xx, 5xx) reach the catch — we surface
  // the same templated fallback used by the backend so the credit always lands.
  function handleCommunityScriptLoad(script: CommunityScript) {
    // Suppress the generic ack by omitting scriptTitle — the Marie intro is the
    // single assistant message that announces this load.
    // `default_image_id` carries the author-paired sample so the loaded script
    // arrives with the matching image already selected in the editor (Sam,
    // 2026-05-13). Null when the author published without an image.
    handleLoadScript(script.code, script.default_image_id ?? null, null, undefined);

    // Fire-and-forget — navigation already happened inside handleLoadScript.
    void (async () => {
      const buildFallback = (): string => {
        const author = script.author_name || "the community";
        const desc = script.description ? ` ${script.description}` : "";
        return `Loaded **${script.title}** by **${author}**.${desc}`;
      };
      let message: string;
      try {
        const res = await marieApi.introduceScript({
          title: script.title,
          author_name: script.author_name,
          description: script.description,
          code: script.code,
          // model: omitted — Marie's selected model lives inside AiPanel's
          // local state and isn't trivially reachable from here. Backend
          // defaults to gpt-5.4, which is fine for this small intro turn.
        });
        message = res.message;
      } catch {
        // Network / 4xx / 5xx — client-side static fallback, same template
        // the backend uses on LLM failure so the credit still lands.
        message = buildFallback();
      }
      const introMessage: ChatMessage = {
        id: `community-intro-${Date.now()}`,
        role: "assistant",
        content: message,
        extractedCode: script.code,
      };
      // handleLoadScript -> resetWorkspace() just reset messages to
      // PYTHON_AI_MESSAGES (the single id="1" greeting). In the common case
      // this intro REPLACES that greeting so the community intro is the
      // opening message — matching the ack-replacement pattern used in
      // handleLoadScript. The append fallback handles any unlikely edge
      // case where messages have already grown between reset and resolution.
      setPythonAiMessages((msgs) => {
        if (msgs.length === 1 && msgs[0].id === "1") {
          return [introMessage];
        }
        return [...msgs, introMessage];
      });
    })();
  }

  // Shared "wipe everything" routine. Used by both the New Script button
  // (which also forces a tab switch to python) and the AI panel's New Chat
  // button (which leaves the active tab alone — see handleClearWorkspaceFromAiPanel).
  // Resets:
  //   • Monaco editor content (imperative + lifted currentCode state)
  //   • loadedScriptId (so the next chat doesn't associate with the prior script)
  //   • scriptLoadKey (signals PythonTab to refresh currentScriptId from
  //     loadedScriptId — also re-applies the now-empty initialCode)
  //   • workspaceClearKey (signals PythonTab to clear its locally-owned
  //     selectedImage / tempImageFile)
  //   • Marie chat history (back to placeholder greeting on the python panel)
  //   • Marie session id (next chat sends new_session: true)
  function resetWorkspace() {
    if (monacoEditorRef.current) {
      monacoEditorRef.current.setValue("");
    }
    setCurrentCode("");
    setLoadedScriptId(null);
    setLoadedScriptTitle(null);
    setScriptLoadKey((k) => k + 1);
    setWorkspaceClearKey((k) => k + 1);
    setPythonAiMessages(PYTHON_AI_MESSAGES);
    armNewSession();
  }

  function handleNewScript() {
    resetWorkspace();
    setActiveTab("python");
  }

  // AI panel's "Start new chat" wants the same full reset as New Script —
  // empty editor, deselected image, null loadedScriptId, fresh chat + session
  // — but it must NOT switch tabs. Greg may click New Chat from Welcome
  // (someday — Welcome has no AiPanel today) or from the Output tab and
  // expects to stay where he is.
  //
  // Note: AiPanel's handleNewChat already resets its own chat history +
  // arms a new session locally. Calling our setPythonAiMessages /
  // armNewSession again here is intentional and idempotent — it keeps
  // the lifted state in lockstep with the panel's local state, since the
  // Output tab also renders an AiPanel sharing the same lifted messages.
  function handleClearWorkspaceFromAiPanel() {
    resetWorkspace();
  }

  // While the token check is in flight, render nothing to avoid a login flash
  if (currentUser === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} />
      </ThemeProvider>
    );
  }

  // ── Maintenance gate ─────────────────────────────────────────
  // Show the full-viewport maintenance screen when:
  //   1. maintenance.enabled is true
  //   2. the user has NOT clicked "Admin? Sign in here" (adminBypass=false)
  //   3. the current user is NOT an admin
  // Admins always see the normal app + a sticky warning banner. The screen
  // covers anything else (logged-in regular user, anonymous visitor, etc.)
  // — their JWT in localStorage is preserved so they're still logged in
  // when maintenance ends.
  const isAdmin = currentUser !== false && currentUser.is_admin;
  const showMaintenanceScreen =
    maintenance.state?.enabled === true &&
    !maintenance.state.adminBypass &&
    !isAdmin;

  if (showMaintenanceScreen) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenanceScreen
          message={maintenance.state!.message}
          updatedAt={maintenance.state!.updatedAt}
          onAdminLogin={() => maintenance.setAdminBypass(true)}
        />
      </ThemeProvider>
    );
  }

  // Banner shown only to admins while maintenance is on.
  const showMaintenanceBanner =
    maintenance.state?.enabled === true && isAdmin;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {currentUser === false ? (
        <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          {showMaintenanceBanner && (
            <MaintenanceBanner
              message={maintenance.state!.message}
              updatedAt={maintenance.state!.updatedAt}
              onDisable={() => navigate("/admin#maintenance")}
            />
          )}
          <LoginScreen onLogin={handleLogin} ssoError={ssoError ?? undefined} />
        </Box>
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "background.default",
          }}
        >
          {showMaintenanceBanner && (
            <MaintenanceBanner
              message={maintenance.state!.message}
              updatedAt={maintenance.state!.updatedAt}
              onDisable={() => navigate("/admin#maintenance")}
            />
          )}

          {/* Tab bar */}
          <AppHeader
            activeTab={activeTab}
            onTabChange={handleTabChange}
            user={currentUser}
            onSignOut={handleSignOut}
            onSettingsClick={handleSettingsClick}
            colorMode={colorMode}
            onToggleColorMode={toggleColorMode}
          />

          {/* Content area — fills remaining height */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* Main tab content */}
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              {activeTab === "welcome" && (
                <WelcomeTab
                  onNavigateToPython={() => handleTabChange("python")}
                  onNavigateToScripts={(subTab) => {
                    // Sub-tab indices match ScriptsTab: 0 = My Scripts, 1 = Starter Scripts.
                    setScriptsInitialSubTab(subTab);
                    setScriptsSubTabKey((k) => k + 1);
                    handleTabChange("scripts");
                  }}
                  onNavigateToCommunity={() => handleTabChange("community")}
                  onAiPrompt={handleAiPrompt}
                  onLoadScript={handleLoadScript}
                  onLoadCommunityScript={handleCommunityScriptLoad}
                  onChatWithMarie={() => { setActiveTab("python"); setEditorMode("chat"); }}
                  currentUser={currentUser as AuthUser}
                  onWelcomeVideoSeen={handleWelcomeVideoSeen}
                />
              )}
              {/* PythonTab is always mounted so its state (selected image, editor
                  content, etc.) survives tab switches. Visibility is CSS-toggled. */}
              <Box
                sx={{
                  display: activeTab === "python" ? "flex" : "none",
                  flex: 1,
                  overflow: "hidden",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <PythonTab
                  containerWidth={contentWidth}
                  colorMode={colorMode}
                  editorMode={editorMode}
                  onEditorModeChange={setEditorMode}
                  chatMessages={pythonAiMessages}
                  onChatMessagesChange={setPythonAiMessages}
                  onEditorReady={handleEditorReady}
                  onCodeChange={setCurrentCode}
                  onRun={handleRun}
                  isRunning={isRunning}
                  preloadImageId={preloadImageId}
                  onPreloadImageConsumed={() => setPreloadImageId(null)}
                  initialCode={currentCode || undefined}
                  scriptLoadKey={scriptLoadKey}
                  loadedScriptId={loadedScriptId}
                  loadedScriptTitle={loadedScriptTitle}
                  workspaceClearKey={workspaceClearKey}
                  autoAnalyzeError={
                    // PythonTab is ALWAYS mounted (CSS display toggle), so its
                    // Marie panel would also consume errorAnalysisRequest even
                    // while hidden behind the Output tab — double-firing the
                    // analysis. Only feed it the trigger when the Output tab is
                    // NOT the active consumer. When activeTab === "output", the
                    // OutputTab's AiPanel (mounted only then) owns consumption.
                    activeTab === "output" ? null : errorAnalysisRequest
                  }
                  onAutoAnalyzeErrorConsumed={() => setErrorAnalysisRequest(null)}
                  imageFlashKey={imageFlashKey}
                  pendingPrompt={pendingPrompt}
                  onPendingPromptConsumed={() => setPendingPrompt(null)}
                  onNewScript={handleNewScript}
                  onLoadScript={handleLoadScript}
                  onClearWorkspace={handleClearWorkspaceFromAiPanel}
                  marieSessionId={marieSessionId}
                  onMarieSessionIdChange={setMarieSessionId}
                  newSessionArmKey={newSessionArmKey}
                  onArmNewSession={armNewSession}
                  lastResult={lastResult}
                  allowedModels={
                    // currentUser is `AuthUser | null | false`. Narrow with a
                    // typeof check — only an object carries the field.
                    typeof currentUser === "object" && currentUser !== null
                      ? currentUser.allowed_models ?? null
                      : null
                  }
                  onTierLimitToast={(info) => {
                    // Map AiPanel's two kinds to the right snack severity.
                    // Model-not-allowed is "info" — we already auto-swapped
                    // the picker for them, so this is a heads-up not a
                    // failure. Quota-exceeded is "warning" — the input
                    // is now locked for the session.
                    setTierLimitSnack({
                      message: info.message,
                      severity:
                        info.kind === "quota_exceeded_marie_chat"
                          ? "warning"
                          : "info",
                    });
                  }}
                  selectedMarieModel={selectedMarieModel}
                  onSelectedMarieModelChange={setSelectedMarieModel}
                />
              </Box>
              {activeTab === "output" && (
                <OutputTab
                  containerWidth={contentWidth}
                  result={lastResult}
                  inputImageId={lastRunImageId}
                  inputImageFile={lastRunImageFile}
                  chatMessages={pythonAiMessages}
                  onChatMessagesChange={setPythonAiMessages}
                  onRun={handleRunFromOutput}
                  isRunning={isRunning}
                  autoAnalyzeError={errorAnalysisRequest}
                  onAutoAnalyzeErrorConsumed={() => setErrorAnalysisRequest(null)}
                  getEditorCode={() => monacoEditorRef.current?.getValue() ?? currentCode}
                  onInjectCode={(code) => {
                    if (monacoEditorRef.current) {
                      monacoEditorRef.current.setValue(code);
                    }
                    setCurrentCode(code);
                    setScriptLoadKey((k) => k + 1);
                  }}
                  onRestoreCode={(code) => {
                    // In-session undo from the Output tab's AI panel — mirror
                    // the inject path. We don't touch loadedScriptId or armNew
                    // Session here because restore is purely in-session: the
                    // user is rewinding their working copy, not loading a
                    // different saved script.
                    if (monacoEditorRef.current) {
                      monacoEditorRef.current.setValue(code);
                    }
                    setCurrentCode(code);
                    setScriptLoadKey((k) => k + 1);
                  }}
                  marieSessionId={marieSessionId}
                  onMarieSessionIdChange={setMarieSessionId}
                  newSessionArmKey={newSessionArmKey}
                  onArmNewSession={armNewSession}
                  onClearWorkspace={handleClearWorkspaceFromAiPanel}
                  allowedModels={
                    // currentUser is `AuthUser | null | false`. Narrow with a
                    // typeof check — only an object carries the field.
                    typeof currentUser === "object" && currentUser !== null
                      ? currentUser.allowed_models ?? null
                      : null
                  }
                  onTierLimitToast={(info) => {
                    setTierLimitSnack({
                      message: info.message,
                      severity:
                        info.kind === "quota_exceeded_marie_chat"
                          ? "warning"
                          : "info",
                    });
                  }}
                  selectedMarieModel={selectedMarieModel}
                  onSelectedMarieModelChange={setSelectedMarieModel}
                />
              )}
              {activeTab === "imageLibrary" && <ImageLibraryTab />}
              {activeTab === "scripts" && (
                <ScriptsTab
                  onLoadScript={handleLoadScript}
                  initialSubTab={scriptsInitialSubTab}
                  initialSubTabKey={scriptsSubTabKey}
                />
              )}
              {activeTab === "community" && (
                <CommunityTab
                  onLoad={handleLoadScript}
                  onLoadCommunityScript={handleCommunityScriptLoad}
                  onNavigateToMyScripts={() => {
                    // My Scripts is sub-tab index 0 after the 2026-05-15 swap.
                    setScriptsInitialSubTab(0);
                    setScriptsSubTabKey((k) => k + 1);
                    setActiveTab("scripts");
                  }}
                />
              )}
              {activeTab === "help" && <HelpTab />}
              {activeTab === "settings" && (
                <SettingsTab
                  colorMode={colorMode}
                  onToggleColorMode={toggleColorMode}
                />
              )}
            </Box>

          </Box>
        </Box>
      )}

      {/* ── No-image Run guard snackbar ───────────────────────────
          Fires when handleRun aborts because no image is selected. Lives at
          the ThemeProvider root (rather than inside the main app Box) so it
          stays mounted across tab switches. MUI portals it to body, so the
          DOM parent doesn't affect positioning. Match the bottom-center
          anchor + filled Alert pattern used by the AiPanel restore toast for
          a consistent visual language. */}
      <Snackbar
        open={noImageSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setNoImageSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNoImageSnackbarOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Select an image before running the script.
        </Alert>
      </Snackbar>

      {/* ── Tier-limit snackbar ───────────────────────────────────
          Single chokepoint for both basic-tier rejects:
            • Marie chat 403 (model_not_allowed) — auto-switched the picker
              already; this is the heads-up Snackbar. severity: info.
            • Marie chat 429 (quota_exceeded marie_chat) — input is now
              locked for the session. severity: warning.
            • Script run 429 (quota_exceeded script_run) — Run button
              still available (per-hour cap). severity: warning.
          Same bottom-center anchor + filled Alert pattern as the
          "Select an image" toast Greg already approved. 6-second auto-
          hide so the friendly-but-not-tiny message has time to read but
          doesn't camp on screen. */}
      <Snackbar
        open={tierLimitSnack !== null}
        autoHideDuration={6000}
        onClose={() => setTierLimitSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {tierLimitSnack !== null ? (
          <Alert
            onClose={() => setTierLimitSnack(null)}
            severity={tierLimitSnack.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {tierLimitSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* ── One-time data-logging notice (Feature 2) ──────────────
          Shown once per account for authenticated users only, gated on the
          server flag `logging_notice_acknowledged`. currentUser is
          `AuthUser | null | false` — only render for a real signed-in user
          object (never the login screen / loading / guest). It pops on login
          and is dismissed before the user interacts with anything, so it
          naturally precedes the click-to-open welcome-video onboarding in
          WelcomeTab — the two never share the screen. */}
      <LoggingNoticeModal
        open={
          typeof currentUser === "object" &&
          currentUser !== null &&
          currentUser.logging_notice_acknowledged === false
        }
        onAcknowledge={handleAcknowledgeLoggingNotice}
      />
    </ThemeProvider>
  );
}
